import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CalendarHelpModalProps {
  open: boolean;
  onClose: () => void;
}

interface ConceptItem {
  icon: string;
  title: string;
  description: string;
  example?: string;
  sub?: Array<{ icon: string; title: string; description: string }>;
  isSubSection?: boolean;
}

const CONCEPTS: ConceptItem[] = [
  {
    icon: '📅',
    title: 'Horario fijo',
    description:
      'Habilita la misma franja horaria los mismos días para todas las semanas.',
    example: '"Todos los lunes atiendo de 10 a 14 hs."',
  },
  {
    icon: '📅',
    title: 'Día Específico',
    description:
      'Habilita una franja horaria para un único día. No se repite la semana siguiente.',
    example: '"El próximo sábado puedo atender, pero no los siguientes."',
  },
  {
    icon: '🚫',
    title: 'Bloquear espacios',
    description:
      'Bloquea la agenda en un momento puntual sin afectar tu horario fijo.',
    example: '"Tengo dentista el lunes de 10 a 11. Bloqueo ese espacio solo para esa semana."',
  },
];

const SUB_CONCEPTS = [
  {
    icon: '✅',
    title: 'Estándar',
    description:
      'Habilita esos horarios para pacientes nuevos y para pacientes activos. En esos horarios podés recibir asignaciones de pacientes nuevos y tus pacientes actuales también pueden usarlos para reservar o reagendar.',
  },
  {
    icon: '🔁',
    title: 'Reprogramación',
    description:
      'Habilita esos horarios exclusivamente para tus pacientes activos. No se ofrecen para asignar pacientes nuevos ni se comparten con el equipo de CX para ese fin. Son horarios reservados para que tus pacientes puedan reagendar si lo necesitan.',
  },
];

export const CalendarHelpModal: React.FC<CalendarHelpModalProps> = ({ open, onClose }) => (
  <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
    <DialogContent
      id="calendar-help-modal"
      className="sm:max-w-[32rem] p-0 overflow-hidden"
    >
      {/* Header */}
      <DialogHeader className="px-6 pt-6 pb-4 mb-0 border-b border-gray-100">
        <DialogTitle className="text-[1.0625rem] font-semibold text-[#4a3d73]">
          Cómo funciona tu agenda
        </DialogTitle>
        <p className="text-[0.8125rem] text-gray-500 mt-1 leading-snug">
          Entendé qué hace cada opción para configurar tu disponibilidad.
        </p>
      </DialogHeader>

      {/* Body */}
      <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[60vh]">
        {CONCEPTS.map((c) => (
          <ConceptCard key={c.title} concept={c} />
        ))}

        {/* Sub-section divider */}
        <div className="pt-1">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-px flex-1 bg-[#e0d9f5]" />
            <span className="text-[0.6875rem] font-semibold text-[#8475b3] uppercase tracking-wider whitespace-nowrap">
              Dentro de Horario fijo y Día Específico
            </span>
            <div className="h-px flex-1 bg-[#e0d9f5]" />
          </div>
          <div className="space-y-4">
            {SUB_CONCEPTS.map((c) => (
              <ConceptCard key={c.title} concept={c} />
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2 rounded-full text-sm font-semibold text-white border-none cursor-pointer transition-all hover:brightness-90"
          style={{ background: '#6D57BC' }}
        >
          Entendido
        </button>
      </div>
    </DialogContent>
  </Dialog>
);

const ConceptCard: React.FC<{ concept: { icon: string; title: string; description: string; example?: string } }> = ({
  concept,
}) => (
  <div className="flex gap-3 items-start">
    <span className="text-lg leading-none mt-0.5 shrink-0" aria-hidden>
      {concept.icon}
    </span>
    <div className="min-w-0">
      <p className="text-[0.8125rem] font-semibold text-[#4a3d73] leading-snug mb-0.5">
        {concept.title}
      </p>
      <p className="text-[0.8125rem] text-gray-600 leading-relaxed">
        {concept.description}
      </p>
      {concept.example && (
        <p className="text-[0.75rem] text-gray-400 italic mt-1 leading-snug">
          Ejemplo: {concept.example}
        </p>
      )}
    </div>
  </div>
);

export default CalendarHelpModal;
